import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Search, Users, Truck, FileText, Target, FileSignature, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TIPO_ICONE = {
  clientes: { icon: Users, color: "#1B3A6B", label: "Clientes" },
  equipamentos: { icon: Truck, color: "#F97316", label: "Equipamentos" },
  propostas: { icon: FileText, color: "#16a34a", label: "Propostas" },
  leads: { icon: Target, color: "#a855f7", label: "Leads" },
  contratos: { icon: FileSignature, color: "#0284c7", label: "Contratos" },
};

export default function BuscaGlobal() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);

  const buscar = async (termo) => {
    if (!termo || termo.trim().length < 2) {
      setDados(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/busca-global", { params: { q: termo, limit: 10 } });
      setDados(data);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Sem permissão para busca global");
      } else {
        toast.error("Falha na busca");
      }
      setDados(null);
    } finally { setLoading(false); }
  };

  // debounce + sync URL
  useEffect(() => {
    const t = setTimeout(() => {
      buscar(q);
      if (q) setParams({ q }); else setParams({});
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const renderClientes = (items) => items.map((c) => (
    <Card key={c.id} className="p-3 hover:shadow-md transition" data-testid={`bg-cli-${c.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-bold text-sm">{c.nome}</div>
          <div className="text-xs text-muted-foreground font-mono">{c.cnpj || c.email || "—"}</div>
          <div className="text-xs text-muted-foreground">
            {c.cidade ? `${c.cidade}${c.uf ? "/" + c.uf : ""}` : ""}
            {c.telefone ? ` · ${c.telefone}` : ""}
          </div>
        </div>
        <div className="flex flex-col gap-1 items-end">
          <Badge variant="outline" className="text-[10px]">{c.origem === "locativo" ? "Base" : "CRM"}</Badge>
          {c.bloqueado && <Badge className="bg-red-100 text-red-800 text-[10px]">Bloqueado</Badge>}
        </div>
      </div>
    </Card>
  ));

  const renderEquipamentos = (items) => items.map((e) => (
    <Card key={e.id} className="p-3 hover:shadow-md transition" data-testid={`bg-eq-${e.id}`}>
      <div className="font-mono text-sm font-bold">{e.serial}</div>
      <div className="text-xs text-muted-foreground">
        {e.placa ? `Placa: ${e.placa} · ` : ""}{e.propriedade}
      </div>
      <Badge variant="outline" className="text-[10px] mt-1">{e.status || "—"}</Badge>
    </Card>
  ));

  const renderPropostas = (items) => items.map((p) => (
    <Link key={p.id} to={`/app/propostas/${p.id}`}>
      <Card className="p-3 hover:shadow-md transition cursor-pointer" data-testid={`bg-pr-${p.id}`}>
        <div className="font-mono text-xs font-bold">{p.numero}</div>
        <div className="font-medium text-sm">{p.cliente_nome}</div>
        <div className="text-xs text-muted-foreground">{p.vendedor} · {p.status}</div>
        {p.valor && (
          <div className="text-xs font-bold mt-1" style={{ color: "#1B3A6B" }}>
            {Number(p.valor).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
          </div>
        )}
      </Card>
    </Link>
  ));

  const renderLeads = (items) => items.map((l) => (
    <Link key={l.id} to={`/app/leads/${l.id}`}>
      <Card className="p-3 hover:shadow-md transition cursor-pointer" data-testid={`bg-ld-${l.id}`}>
        <div className="font-medium text-sm">{l.nome || l.empresa}</div>
        <div className="text-xs text-muted-foreground">{l.empresa} · {l.email || l.telefone}</div>
        <Badge variant="outline" className="text-[10px] mt-1">{l.status}</Badge>
      </Card>
    </Link>
  ));

  const renderContratos = (items) => items.map((c) => (
    <Card key={c.id} className="p-3 hover:shadow-md transition" data-testid={`bg-ct-${c.id}`}>
      <div className="font-medium text-sm">{c.cliente_nome}</div>
      <div className="text-xs text-muted-foreground">{c.vendedor} · {c.data_inicio} → {c.data_termino}</div>
      {c.valor && (
        <div className="text-xs font-bold mt-1" style={{ color: "#16a34a" }}>
          {Number(c.valor).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
        </div>
      )}
      {c.signature_status === "pending" && (
        <Badge className="bg-amber-100 text-amber-800 text-[10px] mt-1">Pendente assinatura</Badge>
      )}
    </Card>
  ));

  const RENDERERS = {
    clientes: renderClientes,
    equipamentos: renderEquipamentos,
    propostas: renderPropostas,
    leads: renderLeads,
    contratos: renderContratos,
  };

  return (
    <div className="space-y-5" data-testid="busca-global-page">
      <div className="flex items-center gap-3">
        <Search size={28} style={{ color: "#1B3A6B" }} />
        <div>
          <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>Busca Global</h1>
          <p className="text-sm text-muted-foreground">
            Busca por clientes, equipamentos, propostas, leads e contratos — aceita CNPJ com/sem máscara, sem acento, qualquer caixa.
          </p>
        </div>
      </div>

      <Card className="p-4">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-3 text-muted-foreground" />
          <Input
            className="pl-10 text-lg h-12"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ex: WMS · 93.209.765/0333-92 · 93209765033392 · cabo · serial parcial..."
            data-testid="bg-input"
            autoFocus
          />
        </div>
      </Card>

      {loading && (
        <Card className="p-8 text-center"><Loader2 className="animate-spin inline mr-2" /> Buscando...</Card>
      )}

      {!loading && dados && dados.total === 0 && (
        <Card className="p-8 text-center text-muted-foreground" data-testid="bg-vazio">
          Nenhum resultado encontrado para &quot;{q}&quot;.
        </Card>
      )}

      {!loading && dados && dados.total > 0 && (
        <div className="space-y-5">
          {Object.entries(dados.items).map(([tipo, items]) => {
            if (!items || items.length === 0) return null;
            const Conf = TIPO_ICONE[tipo];
            if (!Conf) return null;
            const Icon = Conf.icon;
            return (
              <div key={tipo} data-testid={`bg-section-${tipo}`}>
                <h2 className="flex items-center gap-2 text-lg font-bold mb-2" style={{ color: Conf.color }}>
                  <Icon size={18} /> {Conf.label} ({items.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {RENDERERS[tipo](items)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
